import { Link } from "react-router-dom";
import { ArrowRight, Building2, Landmark, Hammer, Briefcase, Globe2 } from "lucide-react";

const ROLES = [
  {
    code: "01",
    icon: Building2,
    name: "LRG Holdings",
    role: "Project Lead · Developer · SPV Holder",
    body: "Citizen-owned Botswana enterprise founded by Lefika Ragontse. Originator and lead developer of the Botswana Leather Park. Holds the SPV and coordinates all partners.",
    accent: true,
  },
  {
    code: "02",
    icon: Landmark,
    name: "SEZA",
    role: "Special Economic Zones Authority",
    body: "Licensing, 12% SEZ tax rate, 200% training rebate, industrial land allocation, and fast-track export framework within the Lobatse Special Economic Zone.",
  },
  {
    code: "03",
    icon: Hammer,
    name: "White Line Exotic Leather",
    role: "Italian Exotic Tannery · San Miniato, Tuscany",
    body: "Founded 2001 by Alberto & Domingo Panterani. Tuscany-based tannery specialising in crocodile, alligator, ostrich, shark and stingray skins for high-fashion houses. CITES-compliant, full in-house production.",
  },
  {
    code: "04",
    icon: Hammer,
    name: "Italprogetti S.p.A.",
    role: "Tannery Engineering · Pisa",
    body: "Founded 1977 by Eng. Mario Serrini. World leader in tannery engineering, polypropylene drums, automation and CETP effluent treatment design. 25+ engineers, three Tuscany production plants, 40+ years of turnkey tannery delivery globally.",
  },
  {
    code: "05",
    icon: Hammer,
    name: "Angelo Coppola Srl",
    role: "Italian Luxury Shoe Manufacturer",
    body: "Formal partnership for a leather shoe factory in Botswana. Angelo Coppola produces 350 pairs of luxury shoes per day. Joint proposal covers factory design, equipment specification, workforce training and production scaling.",
  },
  {
    code: "06",
    icon: Briefcase,
    name: "Institutional Framework",
    role: "SEZA · ETP · CEDA · NDP · BDC · NDB",
    body: "Project aligned across Botswana's industrial development institutions: Special Economic Zones Authority, Economic Transformation Programme, Citizen Entrepreneurial Development Agency, National Development Plan, Botswana Development Corporation, and National Development Bank Botswana.",
  },
  {
    code: "07",
    icon: Briefcase,
    name: "Investors",
    role: "Capital Partners",
    body: "Institutional, family office and strategic equity capital deployed via the LRG Leather Botswana SPV across three phased entry options.",
  },
  {
    code: "08",
    icon: Globe2,
    name: "Global Buyers",
    role: "Offtake · Export Markets",
    body: "European fashion houses, premium leather goods producers, automotive interior buyers, AfCFTA partners and SACU/SADC distributors. Six export markets targeted: US, EU, UK, SACU, SADC, AfCFTA.",
  },
];

const TIMELINE = [
  {
    when: "Q4 2026",
    title: "Land Finalization",
    body: "Final SEZA land allocation completed in Lobatse. Master site planning and EIA finalized.",
  },
  {
    when: "Q2 2027",
    title: "Construction Begins",
    body: "Groundworks and tannery shell construction commence. Italian equipment procurement orders placed.",
  },
  {
    when: "Q4 2027",
    title: "Phase 1 Production",
    body: "Tannery commissioning and initial luxury manufacturing lines come online. First export-grade output.",
  },
  {
    when: "Q2 2028",
    title: "Export Scale & Expansion",
    body: "Full-scale export volume to EU/US luxury markets. Phase 2 expansion of finished goods capacity.",
  },
];

export default function Execution() {
  return (
    <div data-testid="page-execution" className="bg-[#F9F9F6]">
      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-16 md:pb-24 max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-9">
            <p className="eyebrow">03 / Partners & Execution</p>
            <h1
              data-testid="execution-headline"
              className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tight mt-6 rise"
            >
              Already organized.
              <br />
              <span className="italic text-[#5C2018]">Already in motion.</span>
            </h1>
            <p className="mt-8 text-base md:text-lg text-[#1A1A1A] max-w-2xl leading-relaxed">
              The roles, the partners, the manufacturing path from raw hide
              to finished luxury good, and the execution timeline through
              first export.
            </p>
          </div>
        </div>
      </section>

      {/* Execution Diagram */}
      <section
        data-testid="execution-diagram"
        className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-24"
      >
        <div className="grid grid-cols-12 gap-8 mb-12 md:mb-16">
          <div className="col-span-12 md:col-span-5">
            <p className="eyebrow">Structure</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05] mt-5">
              Execution structure.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7 self-end">
            <p className="text-base text-[#1A1A1A] leading-relaxed max-w-xl">
              Eight defined roles, one project. LRG Holdings as developer, supported by named Italian technical partners and the full Botswana institutional framework.
            </p>
          </div>
        </div>

        {/* Hub-and-spoke style grid */}
        <div className="relative">
          {/* Top row: LRG Holdings centered */}
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 md:col-start-5 md:col-span-4">
              <RoleNode role={ROLES[0]} primary />
            </div>
          </div>

          {/* Connector line */}
          <div className="hidden md:flex justify-center my-6">
            <div className="w-px h-12 bg-[#1A1A1A]" />
          </div>

          {/* Horizontal divider */}
          <div className="hidden md:block relative my-2">
            <div className="absolute left-[4%] right-[4%] top-1/2 h-px bg-[#1A1A1A]" />
            <div className="h-6" />
          </div>

          {/* Bottom rows: 7 partners in 4-col grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-6">
            {ROLES.slice(1).map((r) => (
              <div key={r.code} className="relative">
                {/* Vertical connector to top divider on desktop */}
                <div className="hidden md:block absolute left-1/2 -top-8 w-px h-8 bg-[#1A1A1A]" />
                <RoleNode role={r} />
              </div>
            ))}
          </div>
        </div>

        <p className="mt-12 text-xs text-[#666666] max-w-3xl">
          Diagram illustrative. LRG Holdings holds and leads the SPV;
          authority flows down to operating partners and capital + offtake
          flow inward.
        </p>
      </section>

      {/* LUXURY LAYER. Raw → Luxury */}
      <section
        data-testid="luxury-layer-section"
        className="bg-[#1A1A1A] text-[#F9F9F6]"
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="grid grid-cols-12 gap-8 mb-14 md:mb-20">
            <div className="col-span-12 md:col-span-7">
              <p className="eyebrow text-[#A8A8A0]">04. The Luxury Layer</p>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] mt-5">
                From raw material
                <br />
                <span className="italic text-[#E5C4A1]">to global luxury.</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-4 md:col-start-9 self-end">
              <p className="text-sm text-[#C7C7BF] leading-relaxed">
                This is not a tannery. It is a vertically integrated luxury
                manufacturing ecosystem. Italian design, Botswana
                production, global export.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#333330] border border-[#333330]">
            {/* Industrial */}
            <figure className="bg-[#1A1A1A] relative overflow-hidden group">
              <img
                src="https://images.pexels.com/photos/17315564/pexels-photo-17315564.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Industrial leather processing in Botswana tannery"
                className="w-full h-[420px] md:h-[560px] object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                data-testid="image-industrial"
              />
              <figcaption className="absolute inset-x-0 bottom-0 p-6 md:p-8 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/70 to-transparent">
                <p className="eyebrow text-[#E5C4A1]">Stage 01</p>
                <p className="font-serif text-2xl md:text-3xl tracking-tight mt-2">
                  Tannery & Processing
                </p>
                <p className="text-sm text-[#C7C7BF] mt-2">
                  BMC hides · Italian-grade wet-blue & crust output
                </p>
              </figcaption>
            </figure>

            {/* Luxury */}
            <figure className="bg-[#1A1A1A] relative overflow-hidden group">
              <img
                src="https://images.unsplash.com/photo-1637868796504-32f45a96d5a0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBsZWF0aGVyJTIwZ29vZHMlMjBiYWclMjBzaG9lc3xlbnwwfHx8fDE3NzcyMzQ0NjJ8MA&ixlib=rb-4.1.0&q=85"
                alt="Finished leather goods. Bags, shoes, accessories"
                className="w-full h-[420px] md:h-[560px] object-cover group-hover:scale-[1.02] transition-transform duration-700"
                data-testid="image-luxury-goods"
              />
              <figcaption className="absolute inset-x-0 bottom-0 p-6 md:p-8 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/70 to-transparent">
                <p className="eyebrow text-[#E5C4A1]">Stage 02</p>
                <p className="font-serif text-2xl md:text-3xl tracking-tight mt-2">
                  Luxury Manufacturing
                </p>
                <p className="text-sm text-[#C7C7BF] mt-2">
                  Bags · shoes · accessories · EU & US export-grade
                </p>
              </figcaption>
            </figure>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#333330] border border-[#333330]">
            {[
              {
                k: "Italian Design",
                v: "Master craftsmen and design houses set the quality bar from day one.",
              },
              {
                k: "Botswana Production",
                v: "Onshore, vertically integrated. Lower cost base, higher traceability.",
              },
              {
                k: "Global Export",
                v: "Finished, high-margin leather goods shipped into EU, US and luxury distribution.",
              },
            ].map((c) => (
              <div key={c.k} className="bg-[#1A1A1A] p-8">
                <p className="eyebrow text-[#A8A8A0]">{c.k}</p>
                <p className="font-serif text-xl md:text-2xl tracking-tight mt-4 leading-tight">
                  {c.v}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section
        data-testid="timeline-section"
        className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32"
      >
        <div className="grid grid-cols-12 gap-8 mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-5">
            <p className="eyebrow">05. Development Timeline</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05] mt-5">
              Time-bound.
              <br />
              Executable.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7 self-end">
            <p className="text-base text-[#1A1A1A] leading-relaxed max-w-xl">
              From land finalization to first export, the project plan moves
              decisively across four defined milestones.
            </p>
          </div>
        </div>

        <ol className="relative">
          {/* vertical line */}
          <div className="absolute left-3 md:left-1/2 top-0 bottom-0 w-px bg-[#1A1A1A] -translate-x-px md:-translate-x-1/2" />
          {TIMELINE.map((t, idx) => (
            <li
              key={t.when}
              data-testid={`timeline-step-${idx}`}
              className="relative grid grid-cols-12 gap-6 py-10 md:py-14"
            >
              {/* Node */}
              <div className="absolute left-3 md:left-1/2 top-12 w-3 h-3 -translate-x-1/2 -translate-y-1/2 bg-[#5C2018]" />
              <div className="absolute left-3 md:left-1/2 top-12 w-7 h-7 border border-[#1A1A1A] -translate-x-1/2 -translate-y-1/2" />

              <div
                className={`col-span-12 md:col-span-5 ${
                  idx % 2 === 0 ? "md:col-start-1 md:text-right md:pr-16" : "md:col-start-8 md:pl-16"
                } pl-12 md:pl-0`}
              >
                <p className="font-serif text-3xl md:text-4xl tracking-tight text-[#5C2018]">
                  {t.when}
                </p>
                <h3 className="font-serif text-2xl tracking-tight mt-3 text-[#1A1A1A]">
                  {t.title}
                </h3>
                <p className="text-sm text-[#666666] mt-3 leading-relaxed max-w-md md:ml-auto">
                  {t.body}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* APPROVAL PATHWAY — 9-step regulatory roadmap */}
      <section className="bg-[#F9F9F6] border-t border-[#E5E5E0]" data-testid="approval-pathway-section">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="grid grid-cols-12 gap-8 mb-12">
            <div className="col-span-12 md:col-span-6">
              <p className="eyebrow">06 — Approval Pathway</p>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05] mt-5">
                Development roadmap.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-5 md:col-start-8 self-end">
              <p className="text-sm text-[#1A1A1A] leading-relaxed">
                Nine regulatory and development steps to first construction. Each must be completed in sequence. Status as of current structuring phase.
              </p>
            </div>
          </div>
          <div className="border-t border-[#1A1A1A]">
            {[
              ["01", "Granted", "SEZA Investor Licence", "Licence No. SEZBW00029 granted to LRG Holdings (Pty) Ltd on 16 March 2026 for setting up a leather processing plant and tannery at Lobatse. 50-year validity."],
              ["02", "In Progress", "Pre-Feasibility Study", "Technical and financial pre-feasibility covering leather processing, solar infrastructure and export logistics."],
              ["03", "In Progress", "Land Allocation — Lobatse SEZ", "Site allocation discussions within the Lobatse Special Economic Zone."],
              ["04", "Proposed", "EIA Screening", "Environmental Impact Assessment screening for tannery operations, effluent treatment and solar development."],
              ["05", "Proposed", "BPC Grid Connection Enquiry", "Application to BPC for grid connection assessment and interconnection study for the solar IPP."],
              ["06", "Proposed", "BERA Licensing Review", "Regulatory review with the Botswana Energy Regulatory Authority for IPP generation licence."],
              ["07", "Next Step", "Offtaker Engagement", "Engagement with BPC and potential Leather Park tenants for power purchase or supply agreements."],
              ["08", "Next Step", "Partner Selection & Financial Close", "Selection of EPC contractor, technical partners and co-investors. Structuring and closing project finance, equity and DFI commitments."],
              ["09", "Future", "Construction & Commissioning", "Phased construction of tannery, manufacturing and solar facilities with progressive commissioning."],
            ].map(([n, status, title, body]) => (
              <div key={n} data-testid={`approval-step-${n}`} className="grid grid-cols-12 gap-4 py-6 border-b border-[#E5E5E0] items-start">
                <span className="col-span-2 md:col-span-1 font-serif text-2xl text-[#5C2018]">{n}</span>
                <span className={`col-span-10 md:col-span-2 text-[10px] uppercase tracking-[0.22em] border px-2 py-1 inline-block w-fit ${
                  status === "Granted" ? "border-[#5C2018] text-[#5C2018] bg-[#5C2018]/10 font-semibold" :
                  status === "In Progress" ? "border-[#5C2018] text-[#5C2018]" :
                  status === "Proposed" ? "border-[#1A1A1A] text-[#1A1A1A]" :
                  status === "Next Step" ? "border-[#666666] text-[#666666]" :
                  "border-[#A8A8A0] text-[#A8A8A0]"
                }`}>{status}</span>
                <div className="col-span-12 md:col-span-9">
                  <p className="font-serif text-xl md:text-2xl tracking-tight leading-snug">{title}</p>
                  <p className="text-sm text-[#666666] mt-2 leading-relaxed max-w-3xl">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#5C2018] text-[#F9F9F6]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28">
          <div className="grid grid-cols-12 gap-8 items-end">
            <div className="col-span-12 md:col-span-8">
              <p className="eyebrow text-[#E5C4A1]">Engage</p>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] mt-5">
                Choose your point
                <br />
                of entry.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-4 flex md:justify-end">
              <Link
                to="/contact"
                data-testid="execution-to-contact-cta"
                className="inline-flex items-center gap-3 px-7 py-4 bg-[#F9F9F6] text-[#1A1A1A] text-[12px] uppercase tracking-[0.2em] font-semibold hover:bg-[#1A1A1A] hover:text-[#F9F9F6] transition-colors duration-300"
              >
                Contact LRG
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function RoleNode({ role, primary = false }) {
  const Icon = role.icon;
  return (
    <div
      data-testid={`role-node-${role.name.toLowerCase().replace(/\s/g, "-")}`}
      className={`p-6 md:p-7 border h-full flex flex-col transition-colors duration-300 ${
        primary
          ? "bg-[#1A1A1A] text-[#F9F9F6] border-[#1A1A1A]"
          : "bg-[#F9F9F6] border-[#1A1A1A] hover:bg-white"
      }`}
    >
      <div className="flex items-center justify-between">
        <span
          className={`font-serif text-xl tracking-tight ${
            primary ? "text-[#E5C4A1]" : "text-[#5C2018]"
          }`}
        >
          {role.code}
        </span>
        <div
          className={`w-9 h-9 border flex items-center justify-center ${
            primary
              ? "border-[#F9F9F6]"
              : "border-[#1A1A1A]"
          }`}
        >
          <Icon size={16} />
        </div>
      </div>
      <h3
        className={`font-serif text-xl md:text-2xl tracking-tight leading-tight mt-6 ${
          primary ? "text-[#F9F9F6]" : "text-[#1A1A1A]"
        }`}
      >
        {role.name}
      </h3>
      <p
        className={`text-[11px] uppercase tracking-[0.2em] mt-2 ${
          primary ? "text-[#E5C4A1]" : "text-[#666666]"
        }`}
      >
        {role.role}
      </p>
      <p
        className={`text-sm leading-relaxed mt-4 ${
          primary ? "text-[#C7C7BF]" : "text-[#1A1A1A]"
        }`}
      >
        {role.body}
      </p>
    </div>
  );
}
